<script setup>
// §8.5 Checkbox — a NATIVE <input type="checkbox">, styled with CSS. Supports
// the indeterminate state (used by DataTable's select-all). The only JS is
// setting the DOM `indeterminate` property, which has no HTML attribute — the
// tick/dash glyphs and every other state are pure CSS.
import { ref, computed, watch, onMounted } from 'vue'
import LIcon from '../base/Icon.vue'

const props = defineProps({
	label: { type: String, default: null },
	groupName: { type: String, default: null },
	value: { type: String, default: null },
	// true | false | 'indeterminate'
	modelValue: { type: String, default: null },
})
const emit = defineEmits(['update:modelValue'])

const el = ref(null)
const checked = computed(() => props.modelValue === props.value)
const indeterminate = computed(() => props.modelValue === 'indeterminate')

function applyIndeterminate()
{
	if (el.value) el.value.indeterminate = indeterminate.value
}
watch(indeterminate, applyIndeterminate)
onMounted(applyIndeterminate)

function onChange(e)
{
	emit('update:modelValue', props.value)	// e.target.checked)
}
</script>

<template>
	<label class="l-checkbox">
		<span class="box">
			<input :nam="groupName" ref="el" type="radio" :value="value" :checked="checked"
				:aria-checked="indeterminate ? 'mixed' : checked" @change="onChange" />
			<LIcon name="circle-dot" :size="10" class="l-mark check" />
		</span>
		<span v-if="label" class="lbl">{{ label }}</span>
	</label>
</template>

<style scoped>
.l-checkbox
{
	display: inline-flex;
	align-items: center;
	gap: var(--sp-2);
	cursor: pointer;
	min-height: var(--target-min);
}

.box
{
	position: relative;
	display: inline-flex;
	width: 18px;
	height: 18px;
	flex: none;
}

input
{
	appearance: none;
	-webkit-appearance: none;
	width: 18px;
	height: 18px;
	margin: 0;
	border-radius: 50%;
	border: 1.5px solid var(--decorative);
	background: var(--surface);
	cursor: pointer;
	transition: background var(--t-fast) var(--ease), border-color var(--t-fast) var(--ease);
}

input:checked,
input:indeterminate
{
	background: var(--interactive);
	border-color: var(--interactive);
}

/* `l-mark`, not `mark` — see the note in Checkbox.vue: the legacy global
   Bootstrap still ships `mark, .mark { background-color: #fff3cd }`. */
.l-mark
{
	position: absolute;
	inset: 0;
	color: var(--on-emphasis);
	background: transparent;
	opacity: 0;
	pointer-events: none;
}

input:checked~.check
{
	opacity: 1;
}

input:indeterminate~.minus
{
	opacity: 1;
}

.lbl
{
	font-size: var(--text-base);
	color: var(--text);
}
</style>
